<template>
  <div class="home">
    <Navbar @on-select-refresh-state="onSelectRefreshState" />
    <div class="flex-grow-1" style="height: 100%">
      <keep-alive>
        <component :is="currentView" :farmId="currentFarmId"></component>
      </keep-alive>
    </div>
  </div>
</template>

<script>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "@vue/runtime-core";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import Navbar from "@/components/Navbar.vue";
import Dashboard from "@/components/Dashboard.vue";
import Farm from "@/components/Farm.vue";

export default {
  name: "MainContent",

  components: {
    Navbar,
    Dashboard,
    Farm,
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const currentView = ref("Dashboard");
    const currentFarmId = ref("");

    const status = computed(() => store.getters.status);

    let intervalId;

    onMounted(async () => {
      // in case browser was refreshed while not viewing dashboard
      router.replace({ name: "dashboard" });

      onSelectRefreshState();

      // set a timer (5 min) to automatically refresh dashboard view
      const interval = 5 * 60000;
      intervalId = setInterval(onIntervalRefreshDashboard, interval);
    });

    onBeforeUnmount(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });

    watch(
      () => route.path,
      async () => {
        if (route.name === "farm") {
          await onSelectFarm(route.params.id);
        } else if (route.name === "dashboard") {
          await onSelectDashboard();
        }
      }
    );

    watch(status, async (newValue) => {
      // watch the vuex network status, change 'currentFarmId' to force a
      // farm map refresh
      if (newValue === "pending") {
        currentFarmId.value = "";
      } else if (newValue === "success") {
        currentFarmId.value = store.getters.currentFarmId;
      }
      await nextTick();
    });

    const onSelectDashboard = async () => {
      if (currentView.value === "Dashboard") {
        return;
      }

      currentFarmId.value = "";
      await nextTick();

      currentView.value = "Dashboard";
    };

    const onSelectFarm = async (farmId) => {
      if (currentView.value === "Farm" && currentFarmId.value === farmId) {
        return;
      }

      store.commit("SET_CURRENT_FARM", farmId);

      // retrieve unit data for all endusers attached to the selected farm. The
      // 'status' watch handler above will update the currentFarmId value.
      try {
        await store.dispatch("refreshCurrentFarmState");
      } catch (err) {
        console.log(err);
      }

      currentView.value = "Farm";
      await nextTick();
    };

    const onSelectRefreshState = async () => {
      try {
        if (currentView.value === "Dashboard") {
          await store.dispatch("refreshAllState");
        } else if (currentView.value === "Farm") {
          await store.dispatch("refreshCurrentFarmState");
        }
      } catch (err) {
        console.log(err);
      }
    };

    const onIntervalRefreshDashboard = async () => {
      try {
        if (currentView.value === "Dashboard") {
          await store.dispatch("refreshAllState");
        }
      } catch (err) {
        console.log(err);
      }
    };

    return {
      currentView,
      currentFarmId,
      onSelectRefreshState,
    };
  },
};
</script>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 100vh;
}
</style>
